import React, { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { Menu, X, ArrowUpRight } from "lucide-react";
import { BRAND } from "../data/content";

const Header = () => {
    const [open, setOpen] = useState(false);
    const [scrolled, setScrolled] = useState(false);
    const location = useLocation();

    useEffect(() => {
        const onScroll = () => setScrolled(window.scrollY > 24);
        window.addEventListener("scroll", onScroll, { passive: true });
        onScroll();
        return () => window.removeEventListener("scroll", onScroll);
    }, []);

    useEffect(() => {
        setOpen(false);
    }, [location.pathname]);

    return (
        <header
            data-testid="site-header"
            className={`fixed top-0 inset-x-0 z-40 transition-all duration-300 ${
                scrolled
                    ? "bg-paper/85 backdrop-blur-md border-b border-ink/10"
                    : "bg-transparent"
            }`}
        >
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-16 sm:h-20 flex items-center justify-between">
                <Link
                    to="/"
                    data-testid="header-logo-link"
                    className="flex items-center gap-3 group"
                >
                    <img
                        src={BRAND.logo}
                        alt="PopIn logo"
                        className="h-9 sm:h-10 w-auto"
                    />
                    <div className="flex flex-col leading-none">
                        <span className="font-marker text-xl sm:text-2xl text-ink tracking-tight">
                            PopInReviews
                            <span className="text-marker-red">.ca</span>
                        </span>
                        <span className="hidden sm:block font-handwriting text-sm text-ink-light -mt-0.5">
                            real reviews · real rooms
                        </span>
                    </div>
                </Link>

                <nav className="hidden md:flex items-center gap-8">
                    <Link
                        to="/"
                        data-testid="nav-reviews-link"
                        className="font-sans font-semibold text-sm text-ink hover:text-marker-red transition-colors"
                    >
                        Reviews
                    </Link>
                    <a
                        href="#events"
                        data-testid="nav-events-link"
                        className="font-sans font-semibold text-sm text-ink hover:text-marker-red transition-colors"
                    >
                        Events
                    </a>
                    <a
                        href="#community"
                        data-testid="nav-community-link"
                        className="font-sans font-semibold text-sm text-ink hover:text-marker-red transition-colors"
                    >
                        Community
                    </a>
                    <a
                        href={BRAND.parentUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        data-testid="nav-cta-button"
                        className="inline-flex items-center gap-1.5 px-4 py-2 bg-ink text-white text-sm font-bold rounded-full hover:bg-marker-red transition-colors"
                    >
                        Try PopIn
                        <ArrowUpRight size={14} strokeWidth={2.5} />
                    </a>
                </nav>

                <button
                    data-testid="mobile-menu-toggle"
                    onClick={() => setOpen(!open)}
                    className="md:hidden p-2 -mr-2 text-ink"
                    aria-label="Toggle menu"
                >
                    {open ? <X size={24} /> : <Menu size={24} />}
                </button>
            </div>

            {open && (
                <div className="md:hidden bg-paper border-t border-ink/10">
                    <div className="px-6 py-6 flex flex-col gap-5">
                        <Link
                            to="/"
                            data-testid="mobile-nav-reviews"
                            className="font-sans font-semibold text-base text-ink"
                        >
                            Reviews
                        </Link>
                        <a
                            href="#events"
                            data-testid="mobile-nav-events"
                            className="font-sans font-semibold text-base text-ink"
                        >
                            Events
                        </a>
                        <a
                            href="#community"
                            data-testid="mobile-nav-community"
                            className="font-sans font-semibold text-base text-ink"
                        >
                            Community
                        </a>
                        <a
                            href={BRAND.parentUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            data-testid="mobile-nav-cta"
                            className="inline-flex items-center gap-1.5 px-5 py-3 bg-ink text-white text-sm font-bold rounded-full w-fit"
                        >
                            Try PopIn
                            <ArrowUpRight size={14} strokeWidth={2.5} />
                        </a>
                    </div>
                </div>
            )}
        </header>
    );
};

export default Header;
